import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLocalization } from "../contexts/LocalizationContext";
import api from "../services/api";
import {
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  User,
  Shield,
  FileText,
  AlertTriangle,
  DollarSign,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Briefcase,
} from "lucide-react";

interface InstitutionInfo {
  institution_code: string;
  institution_name: string;
  category: string;
  email: string;
  business_email: string;
  fraud_email: string;
  finance_email: string;
  contact_person: string;
  contact_phone: string;
  telephone: string;
  website: string;
  address: string;
  city: string;
  country: string;
  sector: string;
  legal_status: string;
  idnat: string;
  nif: string;
  rccm: string;
  info_verified: number;
}

interface MerchantInfoFormProps {
  onComplete: () => void;
}

const STEPS = [
  { id: "company", title: "Company Information", titleFr: "Informations de l'entreprise", icon: Building2 },
  { id: "contacts", title: "Contact Emails", titleFr: "Emails de contact", icon: Mail },
  { id: "legal", title: "Legal Documents", titleFr: "Documents legaux", icon: FileText },
];

const COUNTRIES: { code: string; fr: string; en: string; group: string }[] = [
  { code: "CD", fr: "RD Congo", en: "DR Congo", group: "africa" },
  { code: "ZA", fr: "Afrique du Sud", en: "South Africa", group: "africa" },
  { code: "DZ", fr: "Algerie", en: "Algeria", group: "africa" },
  { code: "AO", fr: "Angola", en: "Angola", group: "africa" },
  { code: "BJ", fr: "Benin", en: "Benin", group: "africa" },
  { code: "BW", fr: "Botswana", en: "Botswana", group: "africa" },
  { code: "BF", fr: "Burkina Faso", en: "Burkina Faso", group: "africa" },
  { code: "BI", fr: "Burundi", en: "Burundi", group: "africa" },
  { code: "CM", fr: "Cameroun", en: "Cameroon", group: "africa" },
  { code: "CV", fr: "Cap-Vert", en: "Cape Verde", group: "africa" },
  { code: "CF", fr: "Centrafrique", en: "Central African Republic", group: "africa" },
  { code: "KM", fr: "Comores", en: "Comoros", group: "africa" },
  { code: "CG", fr: "Congo-Brazzaville", en: "Congo-Brazzaville", group: "africa" },
  { code: "CI", fr: "Cote d'Ivoire", en: "Ivory Coast", group: "africa" },
  { code: "DJ", fr: "Djibouti", en: "Djibouti", group: "africa" },
  { code: "EG", fr: "Egypte", en: "Egypt", group: "africa" },
  { code: "ER", fr: "Erythree", en: "Eritrea", group: "africa" },
  { code: "SZ", fr: "Eswatini", en: "Eswatini", group: "africa" },
  { code: "ET", fr: "Ethiopie", en: "Ethiopia", group: "africa" },
  { code: "GA", fr: "Gabon", en: "Gabon", group: "africa" },
  { code: "GM", fr: "Gambie", en: "Gambia", group: "africa" },
  { code: "GH", fr: "Ghana", en: "Ghana", group: "africa" },
  { code: "GN", fr: "Guinee", en: "Guinea", group: "africa" },
  { code: "GW", fr: "Guinee-Bissau", en: "Guinea-Bissau", group: "africa" },
  { code: "GQ", fr: "Guinee equatoriale", en: "Equatorial Guinea", group: "africa" },
  { code: "KE", fr: "Kenya", en: "Kenya", group: "africa" },
  { code: "LS", fr: "Lesotho", en: "Lesotho", group: "africa" },
  { code: "LR", fr: "Liberia", en: "Liberia", group: "africa" },
  { code: "LY", fr: "Libye", en: "Libya", group: "africa" },
  { code: "MG", fr: "Madagascar", en: "Madagascar", group: "africa" },
  { code: "MW", fr: "Malawi", en: "Malawi", group: "africa" },
  { code: "ML", fr: "Mali", en: "Mali", group: "africa" },
  { code: "MA", fr: "Maroc", en: "Morocco", group: "africa" },
  { code: "MU", fr: "Maurice", en: "Mauritius", group: "africa" },
  { code: "MR", fr: "Mauritanie", en: "Mauritania", group: "africa" },
  { code: "MZ", fr: "Mozambique", en: "Mozambique", group: "africa" },
  { code: "NA", fr: "Namibie", en: "Namibia", group: "africa" },
  { code: "NE", fr: "Niger", en: "Niger", group: "africa" },
  { code: "NG", fr: "Nigeria", en: "Nigeria", group: "africa" },
  { code: "UG", fr: "Ouganda", en: "Uganda", group: "africa" },
  { code: "RW", fr: "Rwanda", en: "Rwanda", group: "africa" },
  { code: "SN", fr: "Senegal", en: "Senegal", group: "africa" },
  { code: "SC", fr: "Seychelles", en: "Seychelles", group: "africa" },
  { code: "SL", fr: "Sierra Leone", en: "Sierra Leone", group: "africa" },
  { code: "SO", fr: "Somalie", en: "Somalia", group: "africa" },
  { code: "SD", fr: "Soudan", en: "Sudan", group: "africa" },
  { code: "SS", fr: "Soudan du Sud", en: "South Sudan", group: "africa" },
  { code: "TZ", fr: "Tanzanie", en: "Tanzania", group: "africa" },
  { code: "TD", fr: "Tchad", en: "Chad", group: "africa" },
  { code: "TG", fr: "Togo", en: "Togo", group: "africa" },
  { code: "TN", fr: "Tunisie", en: "Tunisia", group: "africa" },
  { code: "ZM", fr: "Zambie", en: "Zambia", group: "africa" },
  { code: "ZW", fr: "Zimbabwe", en: "Zimbabwe", group: "africa" },
  { code: "DE", fr: "Allemagne", en: "Germany", group: "europe" },
  { code: "AT", fr: "Autriche", en: "Austria", group: "europe" },
  { code: "BE", fr: "Belgique", en: "Belgium", group: "europe" },
  { code: "BG", fr: "Bulgarie", en: "Bulgaria", group: "europe" },
  { code: "CY", fr: "Chypre", en: "Cyprus", group: "europe" },
  { code: "HR", fr: "Croatie", en: "Croatia", group: "europe" },
  { code: "DK", fr: "Danemark", en: "Denmark", group: "europe" },
  { code: "ES", fr: "Espagne", en: "Spain", group: "europe" },
  { code: "EE", fr: "Estonie", en: "Estonia", group: "europe" },
  { code: "FI", fr: "Finlande", en: "Finland", group: "europe" },
  { code: "FR", fr: "France", en: "France", group: "europe" },
  { code: "GR", fr: "Grece", en: "Greece", group: "europe" },
  { code: "HU", fr: "Hongrie", en: "Hungary", group: "europe" },
  { code: "IE", fr: "Irlande", en: "Ireland", group: "europe" },
  { code: "IT", fr: "Italie", en: "Italy", group: "europe" },
  { code: "LV", fr: "Lettonie", en: "Latvia", group: "europe" },
  { code: "LT", fr: "Lituanie", en: "Lithuania", group: "europe" },
  { code: "LU", fr: "Luxembourg", en: "Luxembourg", group: "europe" },
  { code: "NL", fr: "Pays-Bas", en: "Netherlands", group: "europe" },
  { code: "PL", fr: "Pologne", en: "Poland", group: "europe" },
  { code: "PT", fr: "Portugal", en: "Portugal", group: "europe" },
  { code: "CZ", fr: "Rep. Tcheque", en: "Czech Republic", group: "europe" },
  { code: "RO", fr: "Roumanie", en: "Romania", group: "europe" },
  { code: "GB", fr: "Royaume-Uni", en: "United Kingdom", group: "europe" },
  { code: "RU", fr: "Russie", en: "Russia", group: "europe" },
  { code: "SE", fr: "Suede", en: "Sweden", group: "europe" },
  { code: "CH", fr: "Suisse", en: "Switzerland", group: "europe" },
  { code: "UA", fr: "Ukraine", en: "Ukraine", group: "europe" },
  { code: "AR", fr: "Argentine", en: "Argentina", group: "americas" },
  { code: "BR", fr: "Bresil", en: "Brazil", group: "americas" },
  { code: "CA", fr: "Canada", en: "Canada", group: "americas" },
  { code: "CL", fr: "Chili", en: "Chile", group: "americas" },
  { code: "CO", fr: "Colombie", en: "Colombia", group: "americas" },
  { code: "US", fr: "Etats-Unis", en: "United States", group: "americas" },
  { code: "MX", fr: "Mexique", en: "Mexico", group: "americas" },
  { code: "PE", fr: "Perou", en: "Peru", group: "americas" },
  { code: "SA", fr: "Arabie Saoudite", en: "Saudi Arabia", group: "asia" },
  { code: "AU", fr: "Australie", en: "Australia", group: "asia" },
  { code: "CN", fr: "Chine", en: "China", group: "asia" },
  { code: "KR", fr: "Coree du Sud", en: "South Korea", group: "asia" },
  { code: "AE", fr: "Emirats Arabes Unis", en: "United Arab Emirates", group: "asia" },
  { code: "IN", fr: "Inde", en: "India", group: "asia" },
  { code: "ID", fr: "Indonesie", en: "Indonesia", group: "asia" },
  { code: "IL", fr: "Israel", en: "Israel", group: "asia" },
  { code: "JP", fr: "Japon", en: "Japan", group: "asia" },
  { code: "MY", fr: "Malaisie", en: "Malaysia", group: "asia" },
  { code: "PH", fr: "Philippines", en: "Philippines", group: "asia" },
  { code: "SG", fr: "Singapour", en: "Singapore", group: "asia" },
  { code: "TH", fr: "Thailande", en: "Thailand", group: "asia" },
  { code: "TR", fr: "Turquie", en: "Turkey", group: "asia" },
  { code: "VN", fr: "Vietnam", en: "Vietnam", group: "asia" },
];

const SECTORS: { value: string; fr: string; en: string }[] = [
  { value: "Fintech", fr: "Fintech", en: "Fintech" },
  { value: "E-commerce", fr: "E-commerce", en: "E-commerce" },
  { value: "Telecommunications", fr: "Telecommunications", en: "Telecommunications" },
  { value: "Banking", fr: "Banque", en: "Banking" },
  { value: "Insurance", fr: "Assurance", en: "Insurance" },
  { value: "Transport", fr: "Transport & Logistique", en: "Transport & Logistics" },
  { value: "Energy", fr: "Energie", en: "Energy" },
  { value: "Education", fr: "Education", en: "Education" },
  { value: "Healthcare", fr: "Sante", en: "Healthcare" },
  { value: "Hospitality", fr: "Hotellerie & Tourisme", en: "Hospitality & Tourism" },
  { value: "Food", fr: "Restauration & Alimentation", en: "Food & Beverage" },
  { value: "Agriculture", fr: "Agriculture", en: "Agriculture" },
  { value: "RealEstate", fr: "Immobilier", en: "Real Estate" },
  { value: "NGO", fr: "ONG / Association", en: "NGO / Non-Profit" },
  { value: "Government", fr: "Administration publique", en: "Government / Public Sector" },
  { value: "Gaming", fr: "Jeux & Paris sportifs", en: "Gaming & Betting" },
  { value: "Retail", fr: "Commerce de detail", en: "Retail" },
  { value: "Mining", fr: "Mines & Ressources", en: "Mining & Resources" },
  { value: "Media", fr: "Media & Communication", en: "Media & Communication" },
  { value: "Technology", fr: "Technologie & IT", en: "Technology & IT" },
  { value: "Construction", fr: "Construction & BTP", en: "Construction" },
  { value: "Manufacturing", fr: "Industrie & Production", en: "Manufacturing" },
];

const LEGAL_STATUSES: { value: string; fr: string; en: string }[] = [
  { value: "SARL", fr: "SARL", en: "SARL (Limited Liability Company)" },
  { value: "SA", fr: "SA (Societe Anonyme)", en: "SA (Public Limited Company)" },
  { value: "SAS", fr: "SAS", en: "SAS (Simplified Joint-Stock Company)" },
  { value: "SASU", fr: "SASU", en: "SASU (Single-Shareholder SAS)" },
  { value: "SNC", fr: "SNC (Societe en Nom Collectif)", en: "SNC (General Partnership)" },
  { value: "SCS", fr: "SCS (Societe en Commandite Simple)", en: "SCS (Limited Partnership)" },
  { value: "EI", fr: "Entreprise Individuelle (EI)", en: "Sole Proprietorship (EI)" },
  { value: "EIRL", fr: "EIRL", en: "EIRL (Limited Liability Sole Proprietorship)" },
  { value: "GIE", fr: "Groupement d'Interet Economique (GIE)", en: "Economic Interest Group (GIE)" },
  { value: "Cooperative", fr: "Cooperative", en: "Cooperative" },
  { value: "Association", fr: "Association", en: "Association" },
  { value: "NGO", fr: "ONG", en: "NGO" },
  { value: "PublicInstitution", fr: "Etablissement public", en: "Public Institution" },
  { value: "Branch", fr: "Succursale", en: "Branch Office" },
];

const GROUP_LABELS: Record<string, { fr: string; en: string }> = {
  africa: { fr: "Afrique", en: "Africa" },
  europe: { fr: "Europe", en: "Europe" },
  americas: { fr: "Ameriques", en: "Americas" },
  asia: { fr: "Asie & Oceanie", en: "Asia & Oceania" },
};

const MerchantInfoForm: React.FC<MerchantInfoFormProps> = ({ onComplete }) => {
  const { user } = useAuth();
  const { t, language } = useLocalization();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Partial<InstitutionInfo>>({});

  useEffect(() => {
    fetchInstitutionInfo();
  }, []);

  const fetchInstitutionInfo = async () => {
    try {
      const response = await api.get("/dashboard/institution-info");
      setFormData(response.data);
    } catch (err: any) {
      console.error("Error fetching institution info:", err);
      setError(language === "fr" ? "Erreur lors du chargement" : "Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put("/dashboard/institution-info", formData);
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.detail || (language === "fr" ? "Erreur lors de la sauvegarde" : "Error saving data"));
    } finally {
      setSaving(false);
    }
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) setCurrentStep(currentStep + 1);
    else handleSubmit();
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const renderInput = (label: string, field: string, Icon: any, type = "text", placeholder = "", required = false) => (
    <div key={field}>
      {label && <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">{label}</label>}
      <div className="relative group">
        <div className="absolute left-0 top-0 bottom-0 w-10 flex items-center justify-center pointer-events-none">
          <Icon className="w-4 h-4 text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
        </div>
        <input
          type={type}
          value={(formData as any)[field] || ""}
          onChange={(e) => handleChange(field, e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all"
          placeholder={placeholder}
          required={required}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>{language === "fr" ? "Chargement..." : "Loading..."}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="border-b border-slate-100 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <img src="/LOGO_MOKO_fd_vert.png" alt="MOKO" className="h-9 w-9 rounded-lg object-cover" />
          <div>
            <h1 className="text-sm font-bold text-slate-900">MOKO Afrika</h1>
            <p className="text-[9px] text-indigo-500 font-bold tracking-[0.15em] uppercase">Merchant Suite</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-3xl">
          {/* Title */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-7 h-7 text-indigo-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {language === "fr" ? "Verification de vos informations" : "Verify Your Information"}
            </h2>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              {language === "fr"
                ? "Veuillez verifier et completer les informations de votre entreprise pour assurer un service optimal."
                : "Please verify and complete your company information to ensure optimal service."}
            </p>
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {STEPS.map((step, i) => {
              const StepIcon = step.icon;
              return (
                <React.Fragment key={step.id}>
                  {i > 0 && <div className={`w-12 h-0.5 ${i <= currentStep ? 'bg-indigo-500' : 'bg-slate-200'}`} />}
                  <button
                    onClick={() => setCurrentStep(i)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                      i === currentStep
                        ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                        : i < currentStep
                        ? 'text-indigo-600'
                        : 'text-slate-400'
                    }`}
                  >
                    <StepIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">{language === "fr" ? step.titleFr : step.title}</span>
                    <span className="sm:hidden">{i + 1}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 max-w-xl mx-auto">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Form card */}
          <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-100 p-6 sm:p-8 max-w-xl mx-auto">
            {/* Step 1: Company Info */}
            {currentStep === 0 && (
              <div className="space-y-4">
                {renderInput(language === "fr" ? "Nom de l'entreprise" : "Company name", "institution_name", Building2, "text", "Ex: MOKO Afrika", true)}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">
                      {language === "fr" ? "Secteur d'activite" : "Business sector"}
                    </label>
                    <div className="relative group">
                      <div className="absolute left-0 top-0 bottom-0 w-10 flex items-center justify-center pointer-events-none">
                        <Briefcase className="w-4 h-4 text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                      </div>
                      <select
                        value={
                          SECTORS.some(s => s.value === formData.sector)
                            ? formData.sector
                            : formData.sector ? 'Other' : ''
                        }
                        onChange={(e) => handleChange("sector", e.target.value === 'Other' ? 'Other' : e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all appearance-none"
                      >
                        <option value="">{language === "fr" ? "Selectionnez..." : "Select..."}</option>
                        {SECTORS.map(s => (
                          <option key={s.value} value={s.value}>{language === "fr" ? s.fr : s.en}</option>
                        ))}
                        <option value="Other">{language === "fr" ? "Autre..." : "Other..."}</option>
                      </select>
                    </div>
                    {(formData.sector === 'Other' || (formData.sector && !SECTORS.some(s => s.value === formData.sector) && formData.sector !== '')) && (
                      <input
                        type="text"
                        value={formData.sector === 'Autre' ? '' : formData.sector || ''}
                        onChange={(e) => handleChange("sector", e.target.value)}
                        className="w-full mt-2 pl-4 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all"
                        placeholder={language === "fr" ? "Precisez votre secteur..." : "Specify your sector..."}
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">
                      {language === "fr" ? "Statut juridique" : "Legal status"}
                    </label>
                    <div className="relative group">
                      <div className="absolute left-0 top-0 bottom-0 w-10 flex items-center justify-center pointer-events-none">
                        <FileText className="w-4 h-4 text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                      </div>
                      <select
                        value={
                          LEGAL_STATUSES.some(s => s.value === formData.legal_status)
                            ? formData.legal_status
                            : formData.legal_status ? 'Other' : ''
                        }
                        onChange={(e) => handleChange("legal_status", e.target.value === 'Other' ? 'Other' : e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all appearance-none"
                      >
                        <option value="">{language === "fr" ? "Selectionnez..." : "Select..."}</option>
                        {LEGAL_STATUSES.map(s => (
                          <option key={s.value} value={s.value}>{language === "fr" ? s.fr : s.en}</option>
                        ))}
                        <option value="Other">{language === "fr" ? "Autre..." : "Other..."}</option>
                      </select>
                    </div>
                    {(formData.legal_status === 'Other' || (formData.legal_status && !LEGAL_STATUSES.some(s => s.value === formData.legal_status) && formData.legal_status !== '')) && (
                      <input
                        type="text"
                        value={formData.legal_status === 'Autre' ? '' : formData.legal_status || ''}
                        onChange={(e) => handleChange("legal_status", e.target.value)}
                        className="w-full mt-2 pl-4 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all"
                        placeholder={language === "fr" ? "Precisez le statut..." : "Specify status..."}
                      />
                    )}
                  </div>
                </div>
                {renderInput(language === "fr" ? "Site web" : "Website", "website", Globe, "text", "https://...")}
                {renderInput(language === "fr" ? "Adresse" : "Address", "address", MapPin, "text", language === "fr" ? "Adresse physique" : "Physical address")}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {renderInput(language === "fr" ? "Ville" : "City", "city", MapPin, "text", "Ex: Kinshasa")}
                  <div>
                    <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">
                      {language === "fr" ? "Pays" : "Country"}
                    </label>
                    <select
                      value={formData.country || "CD"}
                      onChange={(e) => handleChange("country", e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all"
                    >
                      <option value="CD">{language === "fr" ? "RD Congo" : "DR Congo"}</option>
                      {["africa", "europe", "americas", "asia"].map(g => (
                        <optgroup key={g} label={language === "fr" ? GROUP_LABELS[g].fr : GROUP_LABELS[g].en}>
                          {COUNTRIES.filter(c => c.group === g && c.code !== "CD").map(c => (
                            <option key={c.code} value={c.code}>{language === "fr" ? c.fr : c.en}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {renderInput(language === "fr" ? "Personne de contact" : "Contact person", "contact_person", User, "text", language === "fr" ? "Nom complet" : "Full name")}
                  {renderInput(language === "fr" ? "Telephone de contact" : "Contact phone", "contact_phone", Phone, "text", "+243...")}
                </div>
                {renderInput(language === "fr" ? "Telephone principal" : "Main phone", "telephone", Phone, "text", "+243...")}
              </div>
            )}

            {/* Step 2: Contact Emails */}
            {currentStep === 1 && (
              <div className="space-y-5">
                <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl mb-2">
                  <p className="text-xs text-indigo-700 leading-relaxed">
                    {language === "fr"
                      ? "Veuillez fournir les adresses email dediees pour chaque type de communication. Cela nous permet de router les demandes au bon departement."
                      : "Please provide dedicated email addresses for each type of communication. This allows us to route requests to the right department."}
                  </p>
                </div>

                {renderInput(language === "fr" ? "Email general" : "General email", "email", Mail, "email", "info@company.com")}

                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="w-4 h-4 text-indigo-500" />
                    <span className="text-sm font-semibold text-slate-800">
                      {language === "fr" ? "Email Business" : "Business Email"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    {language === "fr"
                      ? "Pour les demandes commerciales et les questions liees aux paiements."
                      : "For business-related inquiries and payment questions."}
                  </p>
                  {renderInput("", "business_email", Mail, "email", "payments@company.com")}
                </div>

                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-semibold text-slate-800">
                      {language === "fr" ? "Email Fraude" : "Fraud Email"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    {language === "fr"
                      ? "Pour signaler les transactions suspectes ou les cas de fraude. Cet email recevra des alertes urgentes."
                      : "For reporting suspicious transactions or fraud cases. This email will receive urgent alerts."}
                  </p>
                  {renderInput("", "fraud_email", Shield, "email", "fraud@company.com")}
                </div>

                <div className="border-t border-slate-100 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm font-semibold text-slate-800">
                      {language === "fr" ? "Email Finance" : "Finance Email"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    {language === "fr"
                      ? "Pour les questions financieres : soldes, reconciliations, factures, etc."
                      : "For financial matters: balances, reconciliations, invoices, etc."}
                  </p>
                  {renderInput("", "finance_email", DollarSign, "email", "finance@company.com")}
                </div>
              </div>
            )}

            {/* Step 3: Legal Documents */}
            {currentStep === 2 && (
              <div className="space-y-5">
                <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl mb-2">
                  <p className="text-xs text-amber-700 leading-relaxed">
                    {language === "fr"
                      ? "Ces informations legales sont requises pour la conformite reglementaire. Laissez vide si non applicable."
                      : "These legal details are required for regulatory compliance. Leave blank if not applicable."}
                  </p>
                </div>

                {renderInput("IDNAT (Identification Nationale)", "idnat", FileText, "text", "Ex: 01-J3100-N12345X")}
                {renderInput("NIF (Numero d'Impot Fiscal)", "nif", FileText, "text", "Ex: A1234567Z")}
                {renderInput("RCCM (Registre de Commerce)", "rccm", FileText, "text", "Ex: CD/KIN/RCCM/12-A-34567")}
              </div>
            )}

            {/* Navigation buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
              {currentStep > 0 ? (
                <button
                  onClick={prevStep}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all group"
                >
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  {language === "fr" ? "Precedent" : "Previous"}
                </button>
              ) : (
                <div />
              )}

              <button
                onClick={nextStep}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-60 group"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #5a67d8 100%)' }}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {language === "fr" ? "Sauvegarde..." : "Saving..."}
                  </>
                ) : currentStep < STEPS.length - 1 ? (
                  <>
                    {language === "fr" ? "Suivant" : "Next"}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    {language === "fr" ? "Confirmer et continuer" : "Confirm & Continue"}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* "Skip for now" supprimé : le commentaire d'origine disait "only for testing"
              mais il était parti en prod. Résultat : les marchands cliquaient dessus,
              accédaient à la dashboard sans soumettre, et au refresh suivant le gate
              KYC réapparaissait — d'où la plainte "ça enregistre rien". Supprimé
              2026-06-08 pour forcer le passage par "Confirmer & Continuer". */}
        </div>
      </div>
    </div>
  );
};

export default MerchantInfoForm;
